if (!customElements.get('multi-size-form')) {
  customElements.define(
    'multi-size-form',
    class MultiSizeForm extends HTMLElement {
      connectedCallback() {
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('.multi-size__submit');
        this.countElement = this.querySelector('[data-multi-size-count]');
        this.totalElement = this.querySelector('[data-multi-size-total]');
        this.noneSelectedText = this.countElement.textContent.trim();

        this.moneyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: this.dataset.currency,
        });

        this.tiersElement = this.querySelector('.multi-size__tiers');
        if (this.tiersElement) {
          try {
            this.tiers = JSON.parse(this.tiersElement.querySelector('[data-tier-config]').textContent);
          } catch (error) {
            this.tiers = [];
          }
        }

        this.addEventListener('click', this.onStepClick.bind(this));
        this.addEventListener('change', this.onQuantityChange.bind(this));
        this.addEventListener('input', this.onQuantityInput.bind(this));
        this.addEventListener('keydown', this.onQuantityKeydown.bind(this));
        this.submitButton.addEventListener('click', this.onSubmit.bind(this));

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
      }

      get quantityInputs() {
        return Array.from(this.querySelectorAll('.multi-size__qty-input'));
      }

      get selectedItems() {
        return this.quantityInputs
          .map((input) => ({ id: Number(input.dataset.variantId), quantity: this.readQuantity(input) }))
          .filter((item) => item.quantity > 0);
      }

      onStepClick(event) {
        const button = event.target.closest('button[data-qty-step]');
        if (!button || button.disabled) return;

        const input = button.closest('.multi-size__stepper').querySelector('.multi-size__qty-input');
        const next = this.readQuantity(input) + Number(button.dataset.qtyStep);
        this.setQuantity(input, next);
        this.updateSummary();
      }

      onQuantityChange(event) {
        if (!event.target.classList.contains('multi-size__qty-input')) return;
        this.setQuantity(event.target, this.readQuantity(event.target));
        this.updateSummary();
      }

      // Live feedback while typing a quantity; clamping waits for change/blur.
      onQuantityInput(event) {
        if (!event.target.classList.contains('multi-size__qty-input')) return;

        // The keydown guard stops a decimal point being typed, but a paste can
        // still smuggle one in. Keep the whole part rather than stripping the
        // separator, which would turn a pasted "2.7" into 27 garments.
        const whole = String(event.target.value).split(/[.,]/)[0].replace(/[^0-9]/g, '');
        if (whole !== event.target.value) event.target.value = whole;

        this.updateSummary();
      }

      // `type="number"` accepts ".", "-", "+" and "e" from the keyboard, and
      // reports value="" for anything it then considers invalid — which would
      // silently zero the running total mid-keystroke. Quantities are whole
      // garments, so refusing those characters outright is simpler and calmer
      // than repairing the field afterwards.
      onQuantityKeydown(event) {
        if (!event.target.classList.contains('multi-size__qty-input')) return;
        if (event.ctrlKey || event.metaKey) return; // leave copy/paste/select-all alone
        if (['.', ',', '-', '+', 'e', 'E'].includes(event.key)) event.preventDefault();
      }

      get activeTierPercent() {
        if (!this.tiers?.length) return 0;
        const count = this.quantityInputs.reduce((sum, input) => sum + this.readQuantity(input), 0);
        let percent = 0;
        for (const tier of this.tiers) {
          if (count >= tier.min) percent = tier.pct;
        }
        return percent;
      }

      clampQuantity(input, value) {
        const max = input.max === '' ? Infinity : Number(input.max);
        return Math.max(0, Math.min(value, max));
      }

      // The only place a quantity is read out of the DOM. You cannot order half
      // a garment, so a stray decimal is truncated rather than rounded — an
      // order never silently grows past what was typed. Blank (and anything
      // unparseable) counts as 0.
      readQuantity(input) {
        return Math.trunc(Number(input.value)) || 0;
      }

      // The only place a quantity is written back to the DOM. Blank — not "0" —
      // is the canonical empty state, matching the markup's initial value, so a
      // cell stepped or cleared back down to nothing reads as empty rather than
      // as a zero the shopper has to delete. Truncating here as well means no
      // fractional value can reach the field, the total or the cart payload,
      // whatever route it arrived by.
      setQuantity(input, value) {
        const clamped = Math.trunc(this.clampQuantity(input, value));
        input.value = clamped === 0 ? '' : clamped;
      }

      updateSummary() {
        const totals = this.quantityInputs.reduce(
          (acc, input) => {
            const quantity = this.readQuantity(input);
            input.closest('.multi-size__card')?.classList.toggle('multi-size__card--active', quantity > 0);
            acc.count += quantity;
            acc.cents += quantity * Number(input.dataset.price);
            return acc;
          },
          { count: 0, cents: 0 }
        );

        const discountNote = this.querySelector('[data-multi-size-discount]');
        if (totals.count === 0) {
          this.countElement.textContent = this.noneSelectedText;
          this.totalElement.textContent = '';
          if (discountNote) discountNote.textContent = '';
        } else {
          const template = totals.count === 1 ? this.dataset.textItem : this.dataset.textItems;
          this.countElement.textContent = template.replace('[count]', totals.count);

          const percent = this.activeTierPercent;
          if (percent > 0) {
            const discounted = (totals.cents * (100 - percent)) / 100;
            this.totalElement.innerHTML = '';
            const original = document.createElement('s');
            original.className = 'multi-size__total-original';
            original.textContent = this.moneyFormatter.format(totals.cents / 100);
            this.totalElement.append(original, ` ${this.moneyFormatter.format(discounted / 100)}`);
            if (discountNote) discountNote.textContent = (this.dataset.textDiscountNote || '').replace('[percent]', percent);
          } else {
            this.totalElement.textContent = this.moneyFormatter.format(totals.cents / 100);
            if (discountNote) discountNote.textContent = '';
          }
        }

        this.submitButton.toggleAttribute('disabled', totals.count === 0);
        this.updateTiers(totals.count);
        this.updateStickyBar(totals);
      }

      // The sticky journey bar mirrors this form's count and discounted total.
      updateStickyBar(totals) {
        const bar = document.querySelector(`[data-order-sticky-bar][data-section-id="${this.dataset.sectionId}"]`);
        if (!bar) return;

        const countElement = bar.querySelector('[data-sticky-count]');
        const template = totals.count === 1 ? countElement.dataset.textItem : countElement.dataset.textItems;
        countElement.textContent = template.replace('[count]', totals.count);

        const totalElement = bar.querySelector('[data-sticky-total]');
        const cents = (totals.cents * (100 - this.activeTierPercent)) / 100;
        totalElement.textContent = totalElement.dataset.textTotal.replace(
          '[price]',
          this.moneyFormatter.format(cents / 100)
        );
      }

      updateTiers(count) {
        if (!this.tiersElement || !this.tiers?.length) return;

        const countElement = this.tiersElement.querySelector('[data-tier-count]');
        const template =
          count === 1 ? this.tiersElement.dataset.textGarment : this.tiersElement.dataset.textGarments;
        countElement.textContent = template.replace('[count]', count);

        // Piecewise-linear fill: each tier occupies an equal segment of the track.
        const bounds = [0, ...this.tiers.map((tier) => tier.min)];
        let fill = 1;
        for (let i = 0; i < this.tiers.length; i++) {
          if (count < bounds[i + 1]) {
            fill = (i + (count - bounds[i]) / (bounds[i + 1] - bounds[i])) / this.tiers.length;
            break;
          }
        }
        this.tiersElement.querySelector('[data-tier-fill]').style.width = `${Math.min(fill * 100, 100)}%`;

        this.tiersElement.querySelectorAll('[data-tier-label]').forEach((label, index) => {
          label.classList.toggle('is-active', count >= this.tiers[index].min);
        });
        this.tiersElement.querySelectorAll('.multi-size__tiers-marker').forEach((marker, index) => {
          marker.classList.toggle('is-active', count >= this.tiers[index].min);
        });
      }

      // When the product has a personalisation metaobject the branding modal
      // owns the final add-to-cart; otherwise the sizes go straight to cart.
      onSubmit() {
        if (!this.selectedItems.length || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const modal = document.querySelector(`personalisation-modal[data-section-id="${this.dataset.sectionId}"]`);
        if (modal && typeof modal.openModal === 'function') {
          modal.openModal(this);
          return;
        }
        this.submitItems();
      }

      // extraItems: fee-product lines (JSON add). fileItems: lines carrying an
      // artwork File — those must be multipart posts, one per line, because the
      // JSON items array cannot transport files. garmentProps: line item
      // properties stamped on every garment line. Resolves true on success.
      async submitItems({ extraItems = [], fileItems = [], garmentProps = null } = {}) {
        const garments = this.selectedItems.map((item) => (garmentProps ? { ...item, properties: garmentProps } : item));
        if (!garments.length || this.submitButton.getAttribute('aria-disabled') === 'true') return false;
        const items = [...garments, ...extraItems];

        this.handleErrorMessage();
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        try {
          for (const fileItem of fileItems) {
            const formData = new FormData();
            formData.append('id', fileItem.id);
            formData.append('quantity', fileItem.quantity || 1);
            Object.entries(fileItem.properties || {}).forEach(([key, value]) => {
              formData.append(`properties[${key}]`, value);
            });
            if (fileItem.file) formData.append(`properties[${fileItem.artworkKey || 'Artwork'}]`, fileItem.file);

            const fileResponse = await fetch(`${routes.cart_add_url}`, {
              method: 'POST',
              headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
              body: formData,
            }).then((response) => response.json());
            if (fileResponse.status) {
              this.handleErrorMessage(fileResponse.description || this.dataset.textError);
              this.error = true;
              return false;
            }
          }

          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';

          const body = { items };
          if (this.cart) {
            body.sections = this.cart.getSectionsToRender().map((section) => section.id);
            body.sections_url = window.location.pathname;
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = JSON.stringify(body);

          const response = await fetch(`${routes.cart_add_url}`, config).then((res) => res.json());

          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'multi-size-form',
              errors: response.errors || response.description,
              message: response.message,
            });
            this.handleErrorMessage(response.description || this.dataset.textError);
            this.error = true;
            return false;
          }

          if (!this.cart) {
            window.location = window.routes.cart_url;
            return true;
          }

          this.error = false;
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'multi-size-form',
            cartData: response,
          });

          // cart-notification builds its item selector from `key`; a multi-item
          // add response nests items, so surface the first added line's key.
          if (!response.key && response.items?.length) response.key = response.items[0].key;
          this.cart.renderContents(response);

          this.quantityInputs.forEach((input) => this.setQuantity(input, 0));
          this.updateSummary();
          return true;
        } catch (error) {
          console.error(error);
          this.handleErrorMessage(this.dataset.textError);
          return false;
        } finally {
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          this.querySelector('.loading__spinner').classList.add('hidden');
        }
      }

      handleErrorMessage(errorMessage = false) {
        this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }
    }
  );
}
